import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js'
import en from 'react-phone-number-input/locale/en.json'
import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input/input'
import { nanoid } from 'nanoid'
import { type ICountryDropdownItem } from 'shared/ui/PhoneNumberInput/store/types'
import type { IDropdownItem } from 'shared/ui'
import { CountryFlag } from 'shared/ui/CountryFlag/CountryFlag'
import { FLAG_CUSTOM_ICON_BY_COUNTRY } from 'shared/ui/PhoneNumberInput/lib/constants'
import { isCountryCode } from 'shared/lib'

export class PhoneNumberInputStore {
  private _number = ''
  private _country: ICountryDropdownItem
  private _countries: Map<CountryCode, ICountryDropdownItem> = new Map()
  private _disposeNumberValidation: IReactionDisposer | null = null
  private _numberError: string | null = null
  private _numberRequestError: string | null = null
  private _numberValidationTrigger: string | null = null

  constructor() {
    this.countiesList.forEach((country) => {
      const flagIcon = FLAG_CUSTOM_ICON_BY_COUNTRY[country]

      this._countries.set(country, {
        id: country,
        label: en[country],
        labelRight: `+${getCountryCallingCode(country)}`,
        labelWidth: 100,
        ...(flagIcon ? { iconL: flagIcon } : { customIconL: <CountryFlag country={country} /> }),
      })
    })

    this._country = this.defaultCountry
    makeAutoObservable(this)
    this.reactionNumberValidation()
  }

  setCountry = (value: IDropdownItem) => {
    if (isCountryCode(value.id)) {
      this._country = { ...value, id: value.id }
    }
  }

  setCountryById = (value: string) => {
    if (isCountryCode(value)) {
      const country = this._countries.get(value)
      if (country) this.setCountry(country)
    }
  }

  setNumber = (value: string) => {
    this._number = value
  }

  setNumberError = (value: string) => {
    this._numberError = value
  }

  setNumberRequestError = (value: string) => {
    this._numberRequestError = value
  }

  triggerNumberValidation = () => {
    this._numberValidationTrigger = nanoid()
  }

  reset = () => {
    this._number = ''
    this._numberError = null
    this._numberRequestError = null
    this._numberValidationTrigger = null
    this._country = this.defaultCountry
    this._disposeNumberValidation?.()
  }

  reactionNumberValidation = () => {
    this._disposeNumberValidation?.()
    this._disposeNumberValidation = reaction(
      () => [this._numberValidationTrigger, this.isValidNumber],
      ([trigger, isValid]) => {
        this._numberError = trigger && !isValid ? 'Please enter a valid phone number' : null
        if (isValid) this._numberValidationTrigger = null
      },
      {
        fireImmediately: true,
      }
    )
  }

  get countries(): ICountryDropdownItem[] {
    return Array.from(this._countries.values())
  }

  get country(): ICountryDropdownItem {
    return this._country
  }

  get number() {
    return this._number
  }

  get defaultCountry(): ICountryDropdownItem {
    return (
      this._countries.get('US') ?? {
        id: 'US',
        label: 'United States',
        labelRight: '+1',
        labelWidth: 100,
        iconL: 'flagUSA',
      }
    )
  }

  get parsePhoneNumber() {
    return parsePhoneNumber(this._number)
  }

  get isValidNumber() {
    return isValidPhoneNumber(this._number)
  }

  get numberError() {
    return this._numberError
  }

  get numberRequestError() {
    return this._numberRequestError
  }

  get countiesList() {
    return getCountries().sort((a, b) => {
      const nameA = en[a]
      const nameB = en[b]

      if (nameA < nameB) return -1
      if (nameA > nameB) return 1
      return 0
    })
  }
}
